import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plane,
  Mail,
  Loader2,
  Upload,
  FileText,
  Download,
  Check,
  AlertCircle,
  LogOut,
  ArrowRight,
  ShieldCheck,
  Clock,
  RefreshCw,
} from "lucide-react";
import { PORTAL } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "theditch_portal_token";
const MAX_BYTES = 50 * 1024 * 1024;

const STATUS_LABEL = {
  uploaded: "Uploaded",
  reviewed: "Reviewed",
  needs_resubmission: "Needs resubmission",
};
const STATUS_TONE = {
  uploaded: "bg-[#ECF0F4] text-[#0D2F4D]",
  reviewed: "bg-[#C6D21F] text-[#0D2F4D]",
  needs_resubmission: "bg-[#FCE7E6] text-[#A02E2A]",
};

export default function Portal() {
  // Phases: checking | dashboard | login | login_sent | error
  const [phase, setPhase] = useState("checking");
  const [data, setData] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [signing, setSigning] = useState(false);
  const [typedName, setTypedName] = useState("");
  const tokenRef = useRef(null);

  const loadAccess = async (token) => {
    try {
      const { data: payload } = await axios.get(`${API}/portal/access/${token}`);
      tokenRef.current = token;
      localStorage.setItem(STORAGE_KEY, token);
      setData(payload);
      setTypedName(`${payload.client.first_name} ${payload.client.last_name}`.trim());
      setPhase("dashboard");
    } catch (err) {
      localStorage.removeItem(STORAGE_KEY);
      const status = err.response?.status;
      if (status === 410) {
        setErrorMsg("Your portal link has expired. Pop your email below and we'll send a fresh one.");
        setPhase("login");
      } else if (status === 404) {
        setErrorMsg("That portal link isn't recognised — request a new one.");
        setPhase("login");
      } else {
        setErrorMsg("Something went wrong loading your portal.");
        setPhase("error");
      }
    }
  };

  useEffect(() => {
    const urlToken = new URLSearchParams(window.location.search).get("token");
    if (urlToken) {
      window.history.replaceState({}, document.title, "/portal");
      loadAccess(urlToken);
      return;
    }
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) loadAccess(stored);
    else setPhase("login");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only bootstrap
  }, []);

  const sendMagic = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(loginEmail)) {
      toast.error("Pop in a valid email.");
      return;
    }
    setLoginLoading(true);
    try {
      await axios.post(`${API}/portal/login`, { email: loginEmail.trim() });
      setPhase("login_sent");
    } catch {
      toast.error("Couldn't send that — try again.");
    } finally {
      setLoginLoading(false);
    }
  };

  const onUpload = async (categoryId, fileInput) => {
    const file = fileInput.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error("Max file size is 50 MB.");
      fileInput.value = "";
      return;
    }
    const form = new FormData();
    form.append("token", tokenRef.current);
    form.append("category", categoryId);
    form.append("file", file);
    try {
      await axios.post(`${API}/portal/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${file.name} uploaded`);
      await loadAccess(tokenRef.current);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed.");
    } finally {
      fileInput.value = "";
    }
  };

  const onSign = async () => {
    if (typedName.trim().length < 3) {
      toast.error("Type your full name to sign.");
      return;
    }
    setSigning(true);
    try {
      await axios.post(
        `${API}/portal/agreement/sign?token=${tokenRef.current}`,
        { typed_name: typedName.trim() }
      );
      toast.success("Agreement signed — thank you.");
      await loadAccess(tokenRef.current);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't sign — please try again.");
    } finally {
      setSigning(false);
    }
  };

  const signOut = () => {
    localStorage.removeItem(STORAGE_KEY);
    setData(null);
    tokenRef.current = null;
    setPhase("login");
  };

  if (phase === "checking") return <CenterCard><Loader2 size={36} className="mx-auto animate-spin text-[var(--ink)]" /><p className="mt-4 text-sm text-[var(--ocean)]">Loading your portal…</p></CenterCard>;

  if (phase === "login") return (
    <PortalShell>
      <CenterCard>
        <ShieldCheck size={36} className="mx-auto text-[var(--ink)]" />
        <span className="num-tag text-xs block mt-6">Client portal</span>
        <h1 className="font-display text-3xl font-extrabold mt-2 leading-tight">Sign in to your portal</h1>
        <p className="text-[var(--ocean)] mt-3 text-sm leading-relaxed">{errorMsg || "Pop your email below and we'll email you a sign-in link."}</p>
        <form onSubmit={sendMagic} className="mt-6 space-y-3 text-left">
          <input
            data-testid={PORTAL.loginEmail}
            type="email"
            value={loginEmail}
            onChange={(e) => setLoginEmail(e.target.value)}
            placeholder="you@email.com"
            className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-3 text-base focus:outline-none focus:border-[var(--ink)]"
          />
          <button
            type="submit"
            data-testid={PORTAL.loginSubmit}
            disabled={loginLoading}
            className="w-full flex items-center justify-center gap-2 bg-[var(--ink)] text-[var(--paper)] font-display font-bold rounded-xl px-6 py-3.5 hover:bg-[var(--ink-soft)] transition-colors disabled:opacity-60"
          >
            {loginLoading ? <Loader2 size={18} className="animate-spin" /> : <>Email me a sign-in link <ArrowRight size={16} /></>}
          </button>
        </form>
        <p className="mt-5 text-xs text-[var(--ocean)]">Not a client yet? <a href="/" className="underline font-bold">Browse packages →</a></p>
      </CenterCard>
    </PortalShell>
  );

  if (phase === "login_sent") return (
    <PortalShell>
      <CenterCard>
        <Mail size={40} className="mx-auto text-[var(--lime-deep)]" data-testid={PORTAL.loginSent} />
        <h1 className="font-display text-2xl font-extrabold mt-6">Check your inbox</h1>
        <p className="text-[var(--ocean)] mt-3 text-sm leading-relaxed">If your email matches a client account we just sent you a fresh portal link. Have a peek in spam if you don't see it.</p>
        <a href="/" className="mt-6 inline-block btn-ghost text-sm">← Back to the site</a>
      </CenterCard>
    </PortalShell>
  );

  if (phase === "error") return (
    <PortalShell>
      <CenterCard>
        <AlertCircle size={44} className="mx-auto text-[var(--ink)]" />
        <h1 className="font-display text-2xl font-extrabold mt-6">Hmm, that didn't work</h1>
        <p className="text-[var(--ocean)] mt-3 text-sm">{errorMsg}</p>
      </CenterCard>
    </PortalShell>
  );

  // dashboard
  const { client, categories, documents, category_summary, checklist, agreement } = data;
  const docsByCategory = (catId) => documents.filter((d) => d.category === catId);
  const checklistDone = checklist.filter((s) => s.done).length;

  return (
    <PortalShell onSignOut={signOut}>
      <main data-testid={PORTAL.dashboard} className="max-w-5xl mx-auto px-4 pb-20 pt-6">
        {/* Hero */}
        <div className="bg-[var(--paper)] rounded-3xl border border-[var(--line)] p-6 md:p-9 shadow-sm">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
            <div>
              <span className="num-tag text-xs">Client portal</span>
              <h1 className="font-display text-3xl md:text-4xl font-extrabold mt-2 leading-tight">
                G'day {client.first_name || "team"}
              </h1>
              <p className="text-[var(--ocean)] mt-1 text-sm">
                {client.package_title} · purchased {new Date(client.purchased_at).toLocaleDateString()}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-widest font-bold text-[var(--ocean)]">Progress</p>
              <p className="font-display text-2xl font-extrabold leading-none mt-1">
                {checklistDone}/{checklist.length}
              </p>
            </div>
          </div>

          {/* Agreement banner */}
          {!agreement.signed ? (
            <section
              data-testid={PORTAL.agreementBlock}
              className="rounded-2xl bg-[var(--ink)] text-[var(--paper)] p-6 md:p-7 mb-6"
            >
              <div className="flex flex-wrap items-start gap-4 justify-between">
                <div className="max-w-md">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--lime)]">Action needed</span>
                  <p className="font-display text-xl font-extrabold mt-1 leading-tight">Sign your Client Service Agreement</p>
                  <p className="text-[var(--mist)]/80 text-sm mt-2">
                    Review the agreement, then type your full name below to sign. Legally binding under NZ + AU electronic transactions law — same as DocuSign.
                  </p>
                </div>
                <a
                  data-testid={PORTAL.agreementPreview}
                  href={`${API}/portal/agreement/preview/${tokenRef.current}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white/10 text-[var(--paper)] font-display font-bold rounded-xl px-4 py-2 text-sm hover:bg-white/20 transition-colors"
                >
                  <FileText size={14} /> Preview PDF
                </a>
              </div>
              <div className="mt-5 flex flex-col md:flex-row gap-3">
                <input
                  data-testid={PORTAL.agreementName}
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  placeholder="Type your full name as your signature"
                  className="flex-1 rounded-xl bg-[var(--paper)] text-[var(--ink)] px-4 py-3 text-base focus:outline-none"
                />
                <button
                  type="button"
                  data-testid={PORTAL.agreementSign}
                  onClick={onSign}
                  disabled={signing}
                  className="inline-flex items-center justify-center gap-2 bg-[var(--lime)] text-[var(--ink)] font-display font-extrabold rounded-xl px-6 py-3 hover:brightness-95 transition-all disabled:opacity-60"
                >
                  {signing ? <Loader2 size={16} className="animate-spin" /> : <>Sign agreement <Check size={16} /></>}
                </button>
              </div>
            </section>
          ) : (
            <section
              data-testid={PORTAL.agreementBlock}
              className="rounded-2xl border border-[var(--line)] bg-[#F4F7E8] p-5 mb-6 flex flex-wrap items-center justify-between gap-3"
            >
              <div className="flex items-center gap-3">
                <Check size={22} className="text-[var(--lime-deep)]" />
                <div>
                  <p className="font-display font-bold text-[var(--ink)]">Agreement signed</p>
                  <p className="text-xs text-[var(--ocean)]">{agreement.signed_name} · {new Date(agreement.signed_at).toLocaleString()}</p>
                </div>
              </div>
              <a
                data-testid={PORTAL.agreementDownload}
                href={`${API}/portal/agreement/signed/${tokenRef.current}`}
                className="inline-flex items-center gap-2 bg-[var(--ink)] text-[var(--paper)] font-display font-bold rounded-xl px-4 py-2 text-sm hover:bg-[var(--ink-soft)] transition-colors"
              >
                <Download size={14} /> Download signed copy
              </a>
            </section>
          )}

          {/* Checklist */}
          <section className="grid md:grid-cols-2 gap-6">
            <div>
              <h2 className="font-display text-lg font-extrabold mb-3">Your move plan</h2>
              <ol className="space-y-2" data-testid={PORTAL.checklist}>
                {checklist.map((step, idx) => (
                  <li
                    key={step.id}
                    data-testid={PORTAL.checklistStep(step.id)}
                    className="flex items-start gap-3 rounded-xl border border-[var(--line)] bg-white px-4 py-3"
                  >
                    <span className={`shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${step.done ? "bg-[var(--lime)] text-[var(--ink)]" : "bg-[var(--mist-soft)] text-[var(--ocean)]"}`}>
                      {step.done ? <Check size={11} strokeWidth={3} /> : idx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-tight ${step.done ? "text-[var(--ink)]" : "text-[var(--ocean)]"}`}>
                        {step.label || step.id.replace(/_/g, " ")}
                      </p>
                      {step.done && step.done_at && (
                        <p className="text-[10px] uppercase tracking-widest font-bold text-[var(--ocean)] mt-1">
                          <Clock size={9} className="inline mr-1" />{new Date(step.done_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {/* Documents */}
            <div>
              <h2 className="font-display text-lg font-extrabold mb-3">Documents</h2>
              <div className="space-y-3" data-testid={PORTAL.docs}>
                {categories.map((cat) => {
                  const docs = docsByCategory(cat.id);
                  const summary = category_summary[cat.id] || { uploaded: 0, needs_resubmission: 0 };
                  return (
                    <div
                      key={cat.id}
                      data-testid={PORTAL.docCategory(cat.id)}
                      className="rounded-xl border border-[var(--line)] bg-white p-4"
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div>
                          <p className="font-display font-bold text-sm">{cat.label}</p>
                          <p className="text-xs text-[var(--ocean)] mt-0.5">{cat.examples}</p>
                        </div>
                        {summary.needs_resubmission > 0 && (
                          <span className="text-[9px] uppercase tracking-widest font-bold bg-[#FCE7E6] text-[#A02E2A] px-2 py-0.5 rounded-full">
                            {summary.needs_resubmission} needs resubmit
                          </span>
                        )}
                      </div>

                      {docs.length === 0 && (
                        <p className="text-xs text-[var(--ocean)] italic">Nothing uploaded yet.</p>
                      )}
                      {docs.map((doc) => (
                        <div
                          key={doc.id}
                          data-testid={PORTAL.docRow(doc.id)}
                          className="flex items-center justify-between gap-2 mt-2 rounded-lg bg-[var(--mist-soft)] px-3 py-2"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText size={13} className="shrink-0 text-[var(--ocean)]" />
                            <a
                              href={`${API}/portal/document/${doc.id}/file?token=${tokenRef.current}`}
                              className="text-xs font-bold text-[var(--ink)] hover:underline truncate"
                            >
                              {doc.filename}
                            </a>
                          </div>
                          <span className={`text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_TONE[doc.status] || "bg-[#ECF0F4] text-[#0D2F4D]"}`}>
                            {STATUS_LABEL[doc.status] || doc.status}
                          </span>
                        </div>
                      ))}

                      <label
                        className="mt-3 inline-flex items-center gap-2 text-xs font-bold text-[var(--ink)] cursor-pointer hover:text-[var(--lime-deep)] transition-colors"
                        data-testid={PORTAL.docUpload(cat.id)}
                      >
                        <Upload size={13} /> Upload file
                        <input
                          type="file"
                          className="hidden"
                          onChange={(e) => onUpload(cat.id, e.target)}
                        />
                      </label>
                    </div>
                  );
                })}
              </div>
              <p className="mt-3 text-[11px] text-[var(--ocean)]">Max 50 MB per file. PDFs, images, docs all welcome.</p>
            </div>
          </section>
        </div>

        <p className="mt-6 text-xs text-[var(--ocean)] text-center">
          Questions? <a href="mailto:hello@theditchcollective.co.nz" className="underline font-bold">hello@theditchcollective.co.nz</a>
        </p>
      </main>
    </PortalShell>
  );
}

function PortalShell({ children, onSignOut }) {
  return (
    <div className="min-h-screen bg-[var(--cream)] flex flex-col">
      <header className="px-5 md:px-8 py-5 flex items-center justify-between max-w-6xl mx-auto w-full">
        <a href="/" className="inline-flex items-center gap-2 font-display font-extrabold text-[var(--ink)]">
          <Plane size={18} /> The Ditch Collective
        </a>
        {onSignOut && (
          <button onClick={onSignOut} data-testid={PORTAL.signOut} className="text-xs uppercase tracking-widest font-bold text-[var(--ocean)] hover:text-[var(--ink)] flex items-center gap-1.5">
            <LogOut size={14} /> Sign out
          </button>
        )}
      </header>
      {children}
    </div>
  );
}

function CenterCard({ children }) {
  return (
    <div className="flex items-center justify-center min-h-[60vh] px-4">
      <div className="w-full max-w-lg bg-[var(--paper)] rounded-3xl border border-[var(--line)] p-8 md:p-10 text-center shadow-xl">
        {children}
      </div>
    </div>
  );
}
